import { useState, useEffect } from "react";
import {
  Button,
  Box,
  TextField,
  Container,
  Typography,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
} from "@mui/material";
import { useMutation } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { createMatch } from "../../services/match.service";
import { searchLeagues, searchTeams } from "../../services/lookup.service";
import LookupAutocomplete from "../../components/LookupAutocomplete";
import {
  ChampionsLeagueStages,
  LEAGUE_NAME_KEYWORDS,
  MatchType,
  MatchStatus,
} from "../../utils/constants";
import { searchCountries } from "../../utils/countrySearch";
import {
  formatWorldCupNationOptionLabel,
  searchWorldCupNations,
} from "../../utils/worldCupNation";

const defaultValues = {
  leagueId: null,
  type: MatchType.TEAMS,
  round: "",
  status: MatchStatus.UPCOMING,
  homeTeamId: null,
  awayTeamId: null,
  homeCountry: null,
  awayCountry: null,
  homeTeamScore: "",
  awayTeamScore: "",
  startAt: "",
};

const MatchCreate = () => {
  const navigate = useNavigate();
  const [values, setValues] = useState(defaultValues);
  const [errors, setErrors] = useState({});

  const leagueName = values.leagueId?.name?.toLowerCase() || "";
  const isChampionsLeague = leagueName.includes(
    LEAGUE_NAME_KEYWORDS.CHAMPIONS_LEAGUE,
  );
  const isAfcon = leagueName.includes(LEAGUE_NAME_KEYWORDS.AFCON);
  const isWorldCup = leagueName.includes(LEAGUE_NAME_KEYWORDS.WORLD_CUP);
  const isCountryMatch =
    isAfcon || isWorldCup || values.type === MatchType.COUNTRIES;
  const nationSearch = isWorldCup ? searchWorldCupNations : searchCountries;

  const { mutate, isPending, isSuccess, isError, error } = useMutation({
    mutationKey: ["createMatch"],
    mutationFn: (matchData) => createMatch(matchData),
  });

  const handleChange = (event) => {
    const { name, value } = event.target;
    setValues((prev) => ({ ...prev, [name]: value }));
    setErrors((prev) => ({ ...prev, [name]: "" }));
  };

  const handleLeagueChange = (league) => {
    const nextLeagueName = league?.name?.toLowerCase() || "";
    const nextIsAfcon = nextLeagueName.includes(LEAGUE_NAME_KEYWORDS.AFCON);
    const nextIsWorldCup = nextLeagueName.includes(
      LEAGUE_NAME_KEYWORDS.WORLD_CUP,
    );
    setValues((prev) => ({
      ...prev,
      leagueId: league,
      round: "",
      type:
        nextIsAfcon || nextIsWorldCup ? MatchType.COUNTRIES : MatchType.TEAMS,
      homeTeamId: null,
      awayTeamId: null,
      homeCountry: null,
      awayCountry: null,
    }));
    setErrors((prev) => ({ ...prev, leagueId: "" }));
  };

  const handleHomeTeamChange = (team) => {
    setValues((prev) => ({ ...prev, homeTeamId: team }));
    setErrors((prev) => ({ ...prev, homeTeamId: "" }));
  };

  const handleAwayTeamChange = (team) => {
    setValues((prev) => ({ ...prev, awayTeamId: team }));
    setErrors((prev) => ({ ...prev, awayTeamId: "" }));
  };

  const handleHomeCountryChange = (country) => {
    setValues((prev) => ({ ...prev, homeCountry: country }));
    setErrors((prev) => ({ ...prev, homeCountry: "" }));
  };

  const handleAwayCountryChange = (country) => {
    setValues((prev) => ({ ...prev, awayCountry: country }));
    setErrors((prev) => ({ ...prev, awayCountry: "" }));
  };

  const handleTypeChange = (event) => {
    const nextType = event.target.value;
    setValues((prev) => ({
      ...prev,
      type: nextType,
      homeTeamId: null,
      awayTeamId: null,
      homeCountry: null,
      awayCountry: null,
    }));
    setErrors((prev) => ({
      ...prev,
      type: "",
      homeTeamId: "",
      awayTeamId: "",
      homeCountry: "",
      awayCountry: "",
    }));
  };

  const validateForm = () => {
    const newErrors = {};

    if (!values.leagueId) {
      newErrors.leagueId = "League is required";
    }
    if (!values.type) {
      newErrors.type = "Match type is required";
    }

    if (isCountryMatch) {
      if (!values.homeCountry) {
        newErrors.homeCountry = "Home country is required";
      }
      if (!values.awayCountry) {
        newErrors.awayCountry = "Away country is required";
      }
      const homeKey = isWorldCup
        ? values.homeCountry?.shortName
        : values.homeCountry?.code;
      const awayKey = isWorldCup
        ? values.awayCountry?.shortName
        : values.awayCountry?.code;
      if (
        homeKey &&
        awayKey &&
        String(homeKey).toUpperCase() === String(awayKey).toUpperCase()
      ) {
        newErrors.awayCountry = "Home and away countries must be different";
      }
    } else {
      if (!values.homeTeamId) {
        newErrors.homeTeamId = "Home team is required";
      }
      if (!values.awayTeamId) {
        newErrors.awayTeamId = "Away team is required";
      }
      if (
        values.homeTeamId &&
        values.awayTeamId &&
        values.homeTeamId.id === values.awayTeamId.id
      ) {
        newErrors.awayTeamId = "Home and away teams must be different";
      }
    }

    if (!values.startAt) {
      newErrors.startAt = "Start At is required";
    }
    if (values.homeTeamScore && parseInt(values.homeTeamScore) < 0) {
      newErrors.homeTeamScore = "Score cannot be negative";
    }
    if (values.awayTeamScore && parseInt(values.awayTeamScore) < 0) {
      newErrors.awayTeamScore = "Score cannot be negative";
    }
    if (values.startAt) {
      const trimmedDate = values.startAt.trim();
      if (trimmedDate !== "") {
        const dateObj = new Date(trimmedDate);
        if (isNaN(dateObj.getTime())) {
          newErrors.startAt = "Invalid date format";
        }
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (event) => {
    event.preventDefault();

    if (!validateForm()) {
      return;
    }

    const payload = {
      leagueId: values.leagueId.id,
      type: isCountryMatch ? MatchType.COUNTRIES : MatchType.TEAMS,
      status: values.status,
    };

    if (isCountryMatch) {
      if (isWorldCup) {
        payload.homeCountry = String(
          values.homeCountry.shortName || "",
        ).toUpperCase();
        payload.awayCountry = String(
          values.awayCountry.shortName || "",
        ).toUpperCase();
      } else {
        payload.homeCountry = String(
          values.homeCountry.code || "",
        ).toUpperCase();
        payload.awayCountry = String(
          values.awayCountry.code || "",
        ).toUpperCase();
      }
    } else {
      payload.homeTeamId = values.homeTeamId.id;
      payload.awayTeamId = values.awayTeamId.id;
    }

    if (values.round && values.round.trim() !== "") {
      payload.round = values.round;
    }

    if (values.homeTeamScore !== "") {
      payload.homeTeamScore = parseInt(values.homeTeamScore);
    }
    if (values.awayTeamScore !== "") {
      payload.awayTeamScore = parseInt(values.awayTeamScore);
    }
    if (
      values.startAt &&
      typeof values.startAt === "string" &&
      values.startAt.trim().length > 0
    ) {
      const trimmedDate = values.startAt.trim();
      const dateObj = new Date(trimmedDate);
      if (!isNaN(dateObj.getTime()) && dateObj.getTime() > 0) {
        payload.startAt = dateObj.toISOString();
      }
    }

    mutate(payload);
  };

  useEffect(() => {
    if (isSuccess) {
      alert("Match created successfully!");
      navigate("/matches");
    }
  }, [isSuccess, navigate]);

  useEffect(() => {
    if (isError) {
      alert(`Error creating match: ${error || "Unknown error"}`);
    }
  }, [isError, error]);

  return (
    <Container component="main" maxWidth="md">
      <Box sx={{ mt: 2 }}>
        <Typography component="h1" variant="h4" sx={{ mb: 3 }}>
          Create Match
        </Typography>

        <Box component="form" onSubmit={handleSubmit} noValidate>
          <LookupAutocomplete
            label="League"
            value={values.leagueId}
            onChange={handleLeagueChange}
            onSearch={searchLeagues}
            disabled={isPending}
            required
            error={!!errors.leagueId}
            helperText={errors.leagueId}
            getOptionLabel={(option) => option.name || ""}
          />

          <FormControl fullWidth margin="normal" error={!!errors.type}>
            <InputLabel id="type-label">Match Type</InputLabel>

            <Select
              labelId="type-label"
              id="type"
              name="type"
              value={isAfcon || isWorldCup ? MatchType.COUNTRIES : values.type}
              onChange={handleTypeChange}
              disabled={isPending || isAfcon || isWorldCup}
              label="Match Type"
            >
              <MenuItem value={MatchType.TEAMS}>Teams</MenuItem>
              <MenuItem value={MatchType.COUNTRIES}>Countries</MenuItem>
            </Select>
          </FormControl>

          {isChampionsLeague ? (
            <FormControl fullWidth margin="normal">
              <InputLabel id="round-label">Round (optional)</InputLabel>

              <Select
                labelId="round-label"
                id="round"
                name="round"
                value={values.round}
                onChange={handleChange}
                disabled={isPending}
                label="Round (optional)"
              >
                <MenuItem value="">
                  <em>None</em>
                </MenuItem>

                {Object.values(ChampionsLeagueStages).map((stage) => (
                  <MenuItem key={stage} value={stage}>
                    {stage}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          ) : (
            <TextField
              margin="normal"
              fullWidth
              id="round"
              name="round"
              label="Round (optional)"
              value={values.round}
              onChange={handleChange}
              disabled={isPending}
              error={!!errors.round}
              helperText={errors.round}
            />
          )}

          <FormControl fullWidth margin="normal">
            <InputLabel id="status-label">Status</InputLabel>

            <Select
              labelId="status-label"
              id="status"
              name="status"
              value={values.status}
              onChange={handleChange}
              disabled={isPending}
              label="Status"
            >
              <MenuItem value={MatchStatus.UPCOMING}>Upcoming</MenuItem>

              <MenuItem value={MatchStatus.LIVE}>Live</MenuItem>

              <MenuItem value={MatchStatus.FINISHED}>Finished</MenuItem>
            </Select>
          </FormControl>

          <Box sx={{ mt: 2 }}>
            {isCountryMatch ? (
              <LookupAutocomplete
                label="Home Country"
                value={values.homeCountry}
                onChange={handleHomeCountryChange}
                onSearch={nationSearch}
                disabled={isPending}
                required
                error={!!errors.homeCountry}
                helperText={errors.homeCountry}
                getOptionLabel={(option) =>
                  isWorldCup
                    ? formatWorldCupNationOptionLabel(option)
                    : option.name || ""
                }
                isOptionEqualToValue={(option, value) =>
                  isWorldCup
                    ? option.shortName === value.shortName
                    : option.code === value.code
                }
              />
            ) : (
              <LookupAutocomplete
                label="Home Team"
                value={values.homeTeamId}
                onChange={handleHomeTeamChange}
                onSearch={searchTeams}
                disabled={isPending}
                required
                error={!!errors.homeTeamId}
                helperText={errors.homeTeamId}
                getOptionLabel={(option) => option.name || ""}
              />
            )}
          </Box>

          <Box sx={{ mt: 2 }}>
            {isCountryMatch ? (
              <LookupAutocomplete
                label="Away Country"
                value={values.awayCountry}
                onChange={handleAwayCountryChange}
                onSearch={nationSearch}
                disabled={isPending}
                required
                error={!!errors.awayCountry}
                helperText={errors.awayCountry}
                getOptionLabel={(option) =>
                  isWorldCup
                    ? formatWorldCupNationOptionLabel(option)
                    : option.name || ""
                }
                isOptionEqualToValue={(option, value) =>
                  isWorldCup
                    ? option.shortName === value.shortName
                    : option.code === value.code
                }
              />
            ) : (
              <LookupAutocomplete
                label="Away Team"
                value={values.awayTeamId}
                onChange={handleAwayTeamChange}
                onSearch={searchTeams}
                disabled={isPending}
                required
                error={!!errors.awayTeamId}
                helperText={errors.awayTeamId}
                getOptionLabel={(option) => option.name || ""}
              />
            )}
          </Box>

          <TextField
            margin="normal"
            fullWidth
            id="homeTeamScore"
            name="homeTeamScore"
            label="Home Team Score (optional)"
            type="number"
            value={values.homeTeamScore}
            onChange={handleChange}
            disabled={isPending}
            error={!!errors.homeTeamScore}
            helperText={errors.homeTeamScore}
            inputProps={{ min: 0 }}
          />

          <TextField
            margin="normal"
            fullWidth
            id="awayTeamScore"
            name="awayTeamScore"
            label="Away Team Score (optional)"
            type="number"
            value={values.awayTeamScore}
            onChange={handleChange}
            disabled={isPending}
            error={!!errors.awayTeamScore}
            helperText={errors.awayTeamScore}
            inputProps={{ min: 0 }}
          />

          <TextField
            margin="normal"
            fullWidth
            id="startAt"
            name="startAt"
            label="Start At"
            type="datetime-local"
            value={values.startAt}
            onChange={handleChange}
            disabled={isPending}
            error={!!errors.startAt}
            helperText={errors.startAt}
            InputLabelProps={{
              shrink: true,
            }}
          />

          <Box sx={{ mt: 3, display: "flex", gap: 2 }}>
            <Button type="submit" variant="contained" disabled={isPending}>
              Create Match
            </Button>

            <Button
              variant="outlined"
              disabled={isPending}
              onClick={() => navigate("/matches")}
            >
              Cancel
            </Button>
          </Box>
        </Box>
      </Box>
    </Container>
  );
};

export default MatchCreate;
